import { cn } from "@/lib/utils";

type ToofiMarkProps = {
  className?: string;
  compact?: boolean;
  variant?: "icon" | "hero" | "glyph";
  transparent?: boolean;
};

const styles = `
  .toofi-cat__shadow {
    animation: toofi-cat-shadow 3.8s ease-in-out infinite;
  }
  .toofi-cat__body {
    filter: drop-shadow(0 8px 14px rgba(37, 99, 235, 0.16));
    transform-origin: center bottom;
    animation: toofi-cat-float 3.8s ease-in-out infinite;
    will-change: transform;
  }
  .toofi-cat__spark {
    transform-origin: center;
    animation: toofi-cat-spark 4.6s ease-in-out infinite;
  }
  .toofi-cat__spark-core {
    transform-origin: center;
    animation: toofi-cat-blink 5.2s ease-in-out infinite;
  }
  .toofi-cat__eye {
    transform-origin: center;
    animation: toofi-cat-eye 5.8s ease-in-out infinite;
  }
  .group:hover .toofi-cat__body {
    transform: translateY(-1px) rotate(-2deg) scale(1.02);
  }
  @keyframes toofi-cat-float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-1.8px) rotate(-1deg) scale(1.01); }
  }
  @keyframes toofi-cat-shadow {
    0%, 100% { opacity: 0.22; transform: scaleX(1); }
    50% { opacity: 0.15; transform: scaleX(0.92); }
  }
  @keyframes toofi-cat-spark {
    0%, 100% { transform: scale(1); opacity: 0.95; }
    52% { transform: scale(1.14); opacity: 1; }
    66% { transform: scale(0.94); opacity: 0.92; }
  }
  @keyframes toofi-cat-blink {
    0%, 100% { transform: scale(1); opacity: 1; }
    49% { transform: scale(1); opacity: 1; }
    53% { transform: scale(0.7); opacity: 0.88; }
    57% { transform: scale(1); opacity: 1; }
  }
  @keyframes toofi-cat-eye {
    0%, 100% { transform: scaleY(1); }
    47% { transform: scaleY(1); }
    50% { transform: scaleY(0.22); }
    54% { transform: scaleY(1); }
  }
  .toofi-hero__walker {
    transform-origin: center bottom;
    animation: toofi-hero-walk 5.8s ease-in-out infinite;
    will-change: transform;
  }
  .toofi-hero__tail {
    transform-origin: 250px 70px;
    animation: toofi-hero-tail 2.2s ease-in-out infinite;
  }
  .toofi-hero__spark {
    transform-origin: center;
    animation: toofi-hero-spark 4.4s ease-in-out infinite;
  }
  .toofi-hero__spark-core {
    transform-origin: center;
    animation: toofi-hero-blink 5.2s ease-in-out infinite;
  }
  .toofi-hero__eye {
    transform-origin: center;
    animation: toofi-hero-eye 5.6s ease-in-out infinite;
  }
  .toofi-hero__leg--front {
    transform-origin: 118px 108px;
    animation: toofi-hero-leg-front 0.9s ease-in-out infinite;
  }
  .toofi-hero__leg--mid {
    transform-origin: 144px 108px;
    animation: toofi-hero-leg-mid 0.9s ease-in-out infinite;
  }
  .toofi-hero__leg--back {
    transform-origin: 172px 108px;
    animation: toofi-hero-leg-back 0.9s ease-in-out infinite;
  }
  @keyframes toofi-hero-walk {
    0%, 100% { transform: translateX(0px) translateY(0px); }
    25% { transform: translateX(18px) translateY(-1px); }
    50% { transform: translateX(38px) translateY(0px); }
    75% { transform: translateX(18px) translateY(-1px); }
  }
  @keyframes toofi-hero-tail {
    0%, 100% { transform: rotate(0deg); }
    50% { transform: rotate(8deg); }
  }
  @keyframes toofi-hero-leg-front {
    0%, 100% { transform: rotate(0deg); }
    50% { transform: rotate(10deg); }
  }
  @keyframes toofi-hero-leg-mid {
    0%, 100% { transform: rotate(8deg); }
    50% { transform: rotate(-6deg); }
  }
  @keyframes toofi-hero-leg-back {
    0%, 100% { transform: rotate(-4deg); }
    50% { transform: rotate(9deg); }
  }
  @keyframes toofi-hero-spark {
    0%, 100% { transform: scale(1); opacity: 0.95; }
    50% { transform: scale(1.12); opacity: 1; }
  }
  @keyframes toofi-hero-blink {
    0%, 100% { transform: scale(1); opacity: 1; }
    52% { transform: scale(0.72); opacity: 0.88; }
    57% { transform: scale(1); opacity: 1; }
  }
  @keyframes toofi-hero-eye {
    0%, 100% { transform: scaleY(1); }
    48% { transform: scaleY(1); }
    51% { transform: scaleY(0.18); }
    55% { transform: scaleY(1); }
  }
`;

function ToofiIcon({ className, compact, transparent }: { className?: string; compact: boolean; transparent: boolean }) {
  return (
    <span
      className={cn(
        "toofi-cat group relative inline-flex shrink-0 items-center justify-center",
        compact ? "h-8 w-8" : "h-14 w-14",
        transparent && "bg-transparent shadow-none",
        className,
      )}
      aria-hidden="true"
    >
      {!transparent ? (
        <span className="toofi-cat__shadow absolute inset-x-[18%] bottom-[8%] h-[18%] rounded-full bg-blue-950/12 blur-[5px]" />
      ) : null}
      <svg
        viewBox="0 0 64 64"
        className={cn("toofi-cat__body relative z-[2]", compact ? "h-7 w-7" : "h-12 w-12")}
        fill="none"
      >
        <defs>
          <linearGradient id="toofi-cat-head" x1="16" y1="8" x2="48" y2="54" gradientUnits="userSpaceOnUse">
            <stop stopColor="var(--athr-brand-soft)" />
            <stop offset="1" stopColor="var(--athr-brand-hover)" />
          </linearGradient>
          <linearGradient id="toofi-cat-ear" x1="22" y1="11" x2="31" y2="26" gradientUnits="userSpaceOnUse">
            <stop stopColor="var(--athr-brand-mist)" />
            <stop offset="1" stopColor="var(--athr-brand-soft)" />
          </linearGradient>
        </defs>
        <path d="M19.5 18.5L25.8 10.4C27.2 8.6 29.9 8.5 31.5 10.1L33.3 12L30.7 23.5C29.9 27.1 25.2 28 23 24.9L19.8 20.5C19.2 19.7 19.1 19.1 19.5 18.5Z" fill="url(#toofi-cat-head)" />
        <path d="M44.5 18.5L38.2 10.4C36.8 8.6 34.1 8.5 32.5 10.1L30.7 12L33.3 23.5C34.1 27.1 38.8 28 41 24.9L44.2 20.5C44.8 19.7 44.9 19.1 44.5 18.5Z" fill="url(#toofi-cat-head)" />
        <path d="M23.7 18.8L27.8 13.5C28.7 12.3 30.4 12.3 31.4 13.4L31.9 14L30.1 21.7C29.6 24.1 26.6 24.7 25.1 22.7L23 19.9C22.6 19.3 22.6 19 23.7 18.8Z" fill="url(#toofi-cat-ear)" fillOpacity="0.9" />
        <path d="M40.3 18.8L36.2 13.5C35.3 12.3 33.6 12.3 32.6 13.4L32.1 14L33.9 21.7C34.4 24.1 37.4 24.7 38.9 22.7L41 19.9C41.4 19.3 41.4 19 40.3 18.8Z" fill="url(#toofi-cat-ear)" fillOpacity="0.9" />
        <path d="M32 16.2C20.6891 16.2 12 24.6137 12 35.3C12 45.9863 20.6891 54.4 32 54.4C43.3109 54.4 52 45.9863 52 35.3C52 24.6137 43.3109 16.2 32 16.2Z" fill="url(#toofi-cat-head)" />
        <path d="M20.6 34.6C20.6 28.7 25.6 23.8 31.8 23.8H32.2C38.4 23.8 43.4 28.7 43.4 34.6V38.4C43.4 43.9 38.9 48.4 33.4 48.4H30.6C25.1 48.4 20.6 43.9 20.6 38.4V34.6Z" fill="#f8fbff" fillOpacity="0.96" />
        <path d="M24.2 31.6C24.2 29.1 26.2 27.1 28.7 27.1C31.2 27.1 33.2 29.1 33.2 31.6V34.9C33.2 37.4 31.2 39.4 28.7 39.4C26.2 39.4 24.2 37.4 24.2 34.9V31.6Z" fill="white" />
        <path d="M30.8 31.6C30.8 29.1 32.8 27.1 35.3 27.1C37.8 27.1 39.8 29.1 39.8 31.6V34.9C39.8 37.4 37.8 39.4 35.3 39.4C32.8 39.4 30.8 37.4 30.8 34.9V31.6Z" fill="white" />
        <ellipse className="toofi-cat__eye" cx="28.8" cy="33.1" rx="2" ry="3" fill="#0f172a" />
        <ellipse className="toofi-cat__eye" cx="35.2" cy="33.1" rx="2" ry="3" fill="#0f172a" />
        <circle cx="28.2" cy="32" r="0.55" fill="white" />
        <circle cx="34.6" cy="32" r="0.55" fill="white" />
        <path d="M32 35.6L30.8 37.1H33.2L32 35.6Z" fill="#1e3a8a" fillOpacity="0.8" />
        <path d="M30.2 38.9C31.2 39.8 32.8 39.8 33.8 38.9" stroke="#1e3a8a" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M25.7 37.8L18.9 36.5" stroke="var(--athr-brand-hover)" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M25.2 40.6L18.3 41.2" stroke="var(--athr-brand-hover)" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M38.3 37.8L45.1 36.5" stroke="var(--athr-brand-hover)" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M38.8 40.6L45.7 41.2" stroke="var(--athr-brand-hover)" strokeWidth="1.7" strokeLinecap="round" />
        <ellipse cx="24.5" cy="39.2" rx="1.8" ry="1.1" fill="#fda4af" fillOpacity="0.55" />
        <ellipse cx="39.5" cy="39.2" rx="1.8" ry="1.1" fill="#fda4af" fillOpacity="0.55" />
        <circle className="toofi-cat__spark" cx="45.5" cy="20.2" r="3.2" fill="var(--athr-satisfying-gold)" />
        <circle className="toofi-cat__spark-core" cx="45.5" cy="20.2" r="1.1" fill="white" />
      </svg>
    </span>
  );
}

function ToofiHero({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "toofi-hero relative h-28 w-full overflow-hidden rounded-[1.5rem] border border-blue-100/70 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.95),rgba(219,234,254,0.78)_40%,rgba(191,219,254,0.58)_100%)]",
        className,
      )}
      aria-hidden="true"
    >
      <div className="absolute inset-x-4 bottom-3 h-[10px] rounded-full bg-blue-900/10 blur-[6px]" />
      <div className="absolute inset-x-0 bottom-5 border-t border-dashed border-blue-200/70" />
      <svg viewBox="0 0 320 120" className="toofi-hero__walker absolute bottom-3 left-6 h-[92px] w-[190px]" fill="none">
        <defs>
          <linearGradient id="toofi-hero-body" x1="84" y1="28" x2="200" y2="100" gradientUnits="userSpaceOnUse">
            <stop stopColor="var(--athr-brand-soft)" />
            <stop offset="1" stopColor="var(--athr-brand-hover)" />
          </linearGradient>
          <linearGradient id="toofi-hero-belly" x1="120" y1="56" x2="170" y2="96" gradientUnits="userSpaceOnUse">
            <stop stopColor="#ffffff" />
            <stop offset="1" stopColor="var(--athr-brand-mist)" />
          </linearGradient>
        </defs>
        <path className="toofi-hero__tail" d="M233 63C253 47 281 53 286 73C288 82 282 88 274 88C266 88 262 82 263 75C264 68 271 66 276 70" stroke="var(--athr-brand-hover)" strokeWidth="12" strokeLinecap="round" />
        <path d="M113 48L133 23C139 16 150 16 156 23L163 31L152 58C148 68 134 71 127 62L111 53C106 50 107 47 113 48Z" fill="url(#toofi-hero-body)" />
        <path d="M194 48L174 23C168 16 157 16 151 23L144 31L155 58C159 68 173 71 180 62L196 53C201 50 200 47 194 48Z" fill="url(#toofi-hero-body)" />
        <path d="M150 38C102 38 68 61 68 87C68 102 82 110 101 110H187C223 110 247 95 247 73C247 54 217 38 150 38Z" fill="url(#toofi-hero-body)" />
        <path d="M104 66C104 54 119 46 145 46C172 46 191 57 191 71V82C191 94 179 103 164 103H131C116 103 104 94 104 82V66Z" fill="url(#toofi-hero-belly)" />
        <ellipse cx="126" cy="66" rx="10" ry="13" fill="white" fillOpacity="0.92" />
        <ellipse cx="170" cy="66" rx="10" ry="13" fill="white" fillOpacity="0.92" />
        <ellipse className="toofi-hero__eye" cx="126" cy="68" rx="4.2" ry="6.1" fill="#0f172a" />
        <ellipse className="toofi-hero__eye" cx="170" cy="68" rx="4.2" ry="6.1" fill="#0f172a" />
        <circle cx="124.7" cy="65.7" r="1.1" fill="white" />
        <circle cx="168.7" cy="65.7" r="1.1" fill="white" />
        <path d="M148 76L143.8 81H152.2L148 76Z" fill="#1e3a8a" fillOpacity="0.78" />
        <path d="M141 86C144 88.8 152 88.8 155 86" stroke="#1e3a8a" strokeWidth="3" strokeLinecap="round" />
        <path d="M119 82L96 77" stroke="var(--athr-brand-hover)" strokeWidth="3" strokeLinecap="round" />
        <path d="M117 91L93 93" stroke="var(--athr-brand-hover)" strokeWidth="3" strokeLinecap="round" />
        <path d="M177 82L200 77" stroke="var(--athr-brand-hover)" strokeWidth="3" strokeLinecap="round" />
        <path d="M179 91L203 93" stroke="var(--athr-brand-hover)" strokeWidth="3" strokeLinecap="round" />
        <ellipse cx="118" cy="87" rx="6" ry="3.4" fill="#fda4af" fillOpacity="0.6" />
        <ellipse cx="178" cy="87" rx="6" ry="3.4" fill="#fda4af" fillOpacity="0.6" />
        <path d="M125 96C131 91 165 91 171 96V103C171 107 167 110 163 110H133C129 110 125 107 125 103V96Z" fill="var(--athr-brand-mist)" />
        <path d="M148 95V108" stroke="var(--athr-brand-hover)" strokeOpacity="0.3" strokeWidth="2" strokeLinecap="round" />
        <path d="M139 99H144" stroke="var(--athr-brand-hover)" strokeOpacity="0.5" strokeWidth="2" strokeLinecap="round" />
        <path d="M152 99H157" stroke="var(--athr-brand-hover)" strokeOpacity="0.5" strokeWidth="2" strokeLinecap="round" />
        <path className="toofi-hero__leg--front" d="M118 108C117 119 111 120 111 120" stroke="var(--athr-brand)" strokeWidth="8" strokeLinecap="round" />
        <path className="toofi-hero__leg--mid" d="M144 108C144 120 138 120 138 120" stroke="var(--athr-brand)" strokeWidth="8" strokeLinecap="round" />
        <path className="toofi-hero__leg--back" d="M172 108C173 120 167 120 167 120" stroke="var(--athr-brand)" strokeWidth="8" strokeLinecap="round" />
        <circle className="toofi-hero__spark" cx="217" cy="28" r="8" fill="var(--athr-satisfying-gold)" />
        <circle className="toofi-hero__spark-core" cx="217" cy="28" r="2.4" fill="white" />
      </svg>
    </div>
  );
}

function ToofiGlyph({ className, compact, transparent }: { className?: string; compact: boolean; transparent: boolean }) {
  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center",
        compact ? "h-7 w-7" : "h-10 w-10",
        className,
      )}
      aria-hidden="true"
    >
      {!transparent ? (
        <span className="absolute inset-[8%] rounded-[28%] bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.98),rgba(219,234,254,0.92)_32%,rgba(147,197,253,0.9)_55%,rgba(37,99,235,0.98)_100%)] shadow-[0_10px_22px_rgba(37,99,235,0.26)]" />
      ) : null}
      <svg
        viewBox="0 0 40 40"
        className={cn("relative z-[1]", compact ? "h-5 w-5" : "h-7 w-7")}
        fill="none"
      >
        <path
          d="M12.5 14.5L16.6 9.8C17.5 8.8 19 8.8 20 9.7L21 10.7L19.5 16.1C19 18 16.6 18.5 15.3 17L12.9 14.9C12.4 14.5 12.2 14.9 12.5 14.5Z"
          fill="#EFF6FF"
          fillOpacity="0.95"
        />
        <path
          d="M27.5 14.5L23.4 9.8C22.5 8.8 21 8.8 20 9.7L19 10.7L20.5 16.1C21 18 23.4 18.5 24.7 17L27.1 14.9C27.6 14.5 27.8 14.9 27.5 14.5Z"
          fill="#EFF6FF"
          fillOpacity="0.95"
        />
        <path
          d="M20 11.8C13.8 11.8 9 16.4 9 22.2C9 28 13.8 32.6 20 32.6C26.2 32.6 31 28 31 22.2C31 16.4 26.2 11.8 20 11.8Z"
          fill="url(#toofi-glyph-body)"
        />
        <path
          d="M13.7 21.7C13.7 18.6 16.3 16.1 19.5 16.1H20.5C23.7 16.1 26.3 18.6 26.3 21.7V23.7C26.3 26.6 23.9 29 21 29H19C16.1 29 13.7 26.6 13.7 23.7V21.7Z"
          fill="white"
          fillOpacity="0.96"
        />
        <ellipse cx="17.2" cy="22.2" rx="1.35" ry="2" fill="#0F172A" />
        <ellipse cx="22.8" cy="22.2" rx="1.35" ry="2" fill="#0F172A" />
        <circle cx="16.8" cy="21.4" r="0.35" fill="white" />
        <circle cx="22.4" cy="21.4" r="0.35" fill="white" />
        <path d="M20 24.2L19.2 25.2H20.8L20 24.2Z" fill="#1D4ED8" fillOpacity="0.82" />
        <path d="M18.6 26.6C19.3 27.2 20.7 27.2 21.4 26.6" stroke="#1D4ED8" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M15.1 25.6L11.3 24.8" stroke="#93C5FD" strokeWidth="1.15" strokeLinecap="round" />
        <path d="M14.8 27.2L11 27.6" stroke="#93C5FD" strokeWidth="1.15" strokeLinecap="round" />
        <path d="M24.9 25.6L28.7 24.8" stroke="#93C5FD" strokeWidth="1.15" strokeLinecap="round" />
        <path d="M25.2 27.2L29 27.6" stroke="#93C5FD" strokeWidth="1.15" strokeLinecap="round" />
        <path d="M28.1 10.1L29.1 12.2L31.3 13.1L29.1 14L28.1 16.1L27.2 14L25 13.1L27.2 12.2L28.1 10.1Z" fill="#FBBF24" />
        <defs>
          <linearGradient id="toofi-glyph-body" x1="11.5" y1="12.8" x2="28.4" y2="30.8" gradientUnits="userSpaceOnUse">
            <stop stopColor="#93C5FD" />
            <stop offset="1" stopColor="#2563EB" />
          </linearGradient>
        </defs>
      </svg>
    </span>
  );
}

export function ToofiMark({ className, compact = false, variant = "icon", transparent = false }: ToofiMarkProps) {
  return (
    <>
      {variant === "hero" ? (
        <ToofiHero className={className} />
      ) : variant === "glyph" ? (
        <ToofiGlyph className={className} compact={compact} transparent={transparent} />
      ) : (
        <ToofiIcon className={className} compact={compact} transparent={transparent} />
      )}
      <style>{styles}</style>
    </>
  );
}
